import { useState, useEffect } from 'react'
import Joyride from 'react-joyride'
import useTooltipTutorialhook from '../hooks/tooltiptutorialhook'
import Loader from '../components/loader'
import { useLocation } from 'react-router-dom'

const TooltipWalkthrough = ({ pageKey, forceRun = false }) => {
  const [run, setRun] = useState(false)
  const [showPrompt, setShowPrompt] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const { filteredSteps, loading } = useTooltipTutorialhook(run)
  const location = useLocation()

  // Detect small devices
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleJoyrideCallback = data => {
    const { status, action, type, index } = data

    if (
      (type === 'step:before' || type === 'step:after') &&
      typeof index === 'number'
    ) {
      window.dispatchEvent(
        new CustomEvent('joyrideStepIndex', {
          detail: { stepIndex: index }
        })
      )
    }

    if (type === 'step:before' && action === 'next') {
      window.dispatchEvent(new CustomEvent('tooltip:start'))
    } else if (
      status === 'finished' ||
      status === 'skipped' ||
      action === 'close'
    ) {
      window.dispatchEvent(
        new CustomEvent('tooltip:end', {
          bubbles: true,
          detail: { isEnding: true }
        })
      )
      setRun(false)

      if (location.pathname === '/ticket-management') {
        const interval = setInterval(() => {
          const targetEl = document.querySelector(
            '[style*="overflow: initial"]'
          )
          if (targetEl) {
            targetEl.style.removeProperty('overflow')
            clearInterval(interval)
          }
        }, 100)
      }
    }
  }

  useEffect(() => {
    const alreadyShown = localStorage.getItem(`joyrideShown-${pageKey}`)

    if (!isMobile) {
      if (forceRun) {
        setRun(true)
      } else if (!alreadyShown) {
        setShowPrompt(true)
      }
    }

    const handleTrigger = () => setRun(true)
    window.addEventListener('triggertutorialwalkthrough', handleTrigger)
    window.addEventListener('startTutorial', handleTrigger)

    return () => {
      window.removeEventListener('triggertutorialwalkthrough', handleTrigger)
      window.removeEventListener('startTutorial', handleTrigger)
    }
  }, [location.pathname, pageKey, forceRun, isMobile])

  const handleStart = () => {
    setRun(true)
    setShowPrompt(false)
    window.dispatchEvent(new CustomEvent('scrollLimit', { detail: true }))
    localStorage.setItem(`joyrideShown-${pageKey}`, 'true')
  }

  const handleSkip = () => {
    setShowPrompt(false)
    localStorage.setItem(`joyrideShown-${pageKey}`, 'true')
  }

  if (loading) {
    return (
      <div className='fixed inset-0 bg-black bg-opacity-25 flex justify-center items-center z-50'>
        <Loader />
      </div>
    )
  }

  return (
    <>
      {!isMobile && showPrompt && location.pathname !== '/subscription' && (
        <div className='fixed inset-0 bg-black bg-opacity-25 flex justify-center items-center z-50'>
          <div className='bg-[#F4F4F4] rounded-[24px] w-[456px] min-h-[240px] border-[1.6px] border-[#FFFFFF] shadow'>
            {/* header */}
            <div className='flex justify-between items-center p-4 border-b'>
              <h2 className='text-lg font-semibold'>Confirmation</h2>
              <button
                onClick={handleSkip}
                className='text-gray-700 hover:text-gray-900'
              >
                <svg width='30' height='30' viewBox='0 0 24 24' fill='none'>
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M7.77 7.77a.925.925 0 0 1 1.3 0L12 10.7l2.93-2.93a.925.925 0 0 1 1.3 1.3L13.3 12l2.93 2.93a.925.925 0 0 1-1.3 1.3L12 13.3l-2.93 2.93a.925.925 0 1 1-1.3-1.3L10.7 12 7.77 9.07a.925.925 0 0 1 0-1.3Z'
                    fill='#373737'
                  />
                </svg>
              </button>
            </div>

            {/* body */}
            <div className='p-4 text-sm text-gray-700'>
              <strong>Welcome!</strong>
              <br />
              This interactive walkthrough will guide you through key features
              on this page to help you get started with ease.
              <br />
              <br />
              Click <strong>Start</strong> to begin the tour.
            </div>

            {/* footer */}
            <div className='flex justify-end gap-4 items-center p-4 border-t'>
              <button
                onClick={handleSkip}
                className='bg-[#F4F4F4] bg-opacity-20 border-black h-[39px] rounded-[59px] text-[14px] text-black pointer-events-auto z-50'
              >
                Skip
              </button>
              <button
                onClick={handleStart}
                className='bg-[#232328] w-[85px] h-[39px] rounded-[59px] text-white text-[14px] pointer-events-auto z-50'
              >
                Start
              </button>
            </div>
          </div>
        </div>
      )}

      {!isMobile && (
        <Joyride
          steps={filteredSteps}
          run={run && filteredSteps.length > 0}
          continuous
          showSkipButton
          showProgress
          showCloseButton
          styles={{
            tooltipTitle: { fontWeight: '600' },
            options: { primaryColor: '#000' },
            tooltip: {
              backgroundColor: '#F4F4F4',
              borderRadius: '24px',
              position: 'relative',
              border: '1.6px solid #FFFFFF',
              color: '#333',
              padding: '10px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              zIndex: 100000
            },
            buttonBack: {
              borderRadius: '59px',
              height: '35px',
              padding: '12px 12px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: '#000000',
              fontSize: '16px'
            },
            buttonNext: {
              borderRadius: '59px',
              padding: '12px 12px',
              display: 'flex',
              lineHeight: '14px',
              justifyContent: 'center',
              alignItems: 'center'
            },
            buttonSkip: {
              borderRadius: '59px',
              padding: '12px 12px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: '#000000',
              fontSize: '16px'
            }
          }}
          locale={{
            back: 'Back',
            close: 'Close',
            last: 'End',
            next: 'Next',
            skip: 'Skip'
          }}
          callback={handleJoyrideCallback}
        />
      )}
    </>
  )
}

export default TooltipWalkthrough
